import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useAppSelector } from '../../hooks/useAppSelector';
import { updateBoard } from '../../store/boardSlice';
import { fetchWorkspaceBoards } from '../../store/workspaceSlice';
import { showToast } from '../../store/uiSlice';
import { Board } from '../../types/common';

interface EditBoardModalProps {
  open: boolean;
  onClose: () => void;
  board: Board | null;
  workspaceId: string;
}

const boardColors = [
  '#0073ea', // Blue
  '#00ca72', // Green
  '#ffcb00', // Yellow
  '#ff6900', // Orange
  '#e44258', // Red
  '#9d77f9', // Purple
  '#ff1493', // Pink
  '#00d4ff', // Cyan
];

const EditBoardModal: React.FC<EditBoardModalProps> = ({ open, onClose, board, workspaceId }) => {
  const dispatch = useAppDispatch();
  const { loading } = useAppSelector((state) => state.boards);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#0073ea',
  });

  const [errors, setErrors] = useState({
    name: '',
  });

  useEffect(() => {
    if (board) {
      setFormData({
        name: board.name || '',
        description: board.description || '',
        color: board.color || '#0073ea',
      });
    }
  }, [board]);

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === 'name' && errors.name) {
      setErrors((prev) => ({ ...prev, name: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {
      name: '',
    };

    if (!formData.name.trim()) {
      newErrors.name = 'Board name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Board name must be at least 2 characters';
    }

    setErrors(newErrors);
    return !newErrors.name;
  };

  const handleSubmit = async () => {
    if (!validateForm() || !board) {
      return;
    }

    try {
      await dispatch(
        updateBoard({
          boardId: board.id,
          data: {
            name: formData.name.trim(),
            description: formData.description.trim() || undefined,
            color: formData.color,
          },
        })
      ).unwrap();

      // Refresh workspace boards
      await dispatch(fetchWorkspaceBoards(workspaceId));

      dispatch(
        showToast({
          message: 'Board updated successfully!',
          type: 'success',
        })
      );

      handleClose();
    } catch (error: any) {
      dispatch(
        showToast({
          message: error.message || 'Failed to update board',
          type: 'error',
        })
      );
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      color: '#0073ea',
    });
    setErrors({ name: '' });
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Board</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <TextField
            fullWidth
            label="Board Name"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            placeholder="e.g., Sprint Planning, Marketing Tasks"
            autoFocus
            required
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Description"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="What is this board for?"
            multiline
            rows={3}
            sx={{ mb: 2 }}
          />

          <Box>
            <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
              Board Color
            </Typography>
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              {boardColors.map((color) => (
                <Box
                  key={color}
                  onClick={() => handleInputChange('color', color)}
                  sx={{
                    backgroundColor: color,
                    width: 40,
                    height: 40,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    border: formData.color === color ? '3px solid #000' : '2px solid transparent',
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'scale(1.1)',
                    },
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !formData.name.trim()}
        >
          {loading ? <CircularProgress size={24} /> : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditBoardModal;
